import { Either } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

const EXCEL_TYPE_REQUEST = 'application/vnd.ms-excel';
const JSON_TYPE_REQUEST = 'application/json';

/**
 * @description Запрос поставщика.
 * @param {string|null} id
 * @param {string} name
 * @param {string} contact
 * @param {string} phone
 * @param {string} email
 * @param {string} address
 * @constructor
 */
export function VendorRequest(
  id = null,
  name = '',
  contact = '',
  phone = '',
  email = '',
  address = '',
) {
  this.getId = () => id;
  this.toQuery = () => ({
    name,
    contact,
    phone,
    email,
    address,
  });
}

/**
 * @description Поставщик.
 * @param {string|null} id
 * @param {string}      name
 * @param {string}      contact
 * @param {string}      phone
 * @param {string}      email
 * @param {string}      address
 * @constructor
 */
export function Vendor(
  id = null,
  name = '',
  contact = '',
  phone = '',
  email = '',
  address = '',
) {
  this.id = id;
  this.name = name;
  this.contact = contact;
  this.phone = phone;
  this.email = email;
  this.address = address;
}

/**
 * @description Шлюз поставщиков.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function VendorGateway(axios) {
  /**
   * @description Просмотр поставщика.
   * @param {string} id
   * @returns {Promise<Either>}
   */
  this.viewVendor = (id) => axios
    .get(`/api/warehouse/vendors/${id}`)
    .then(
      async (response) => Either.right({
        vendor: new Vendor(
          response.data.id,
          response.data.name,
          response.data.contact,
          response.data.phone,
          response.data.email,
          response.data.address,
        ),
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Создание поставщика.
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.create = (params) => axios
    .post('/api/warehouse/vendors', params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Удаление поставщика.
   * @param {string} id Идентификатор поставщика.
   * @returns {Promise<Either>}
   */
  this.delete = (id) => axios
    .delete(`/api/warehouse/vendors/${id}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Редактирование поставщика.
   * @param {string} id
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.edit = (id, params) => axios
    .put(`/api/warehouse/vendors/${id}`, params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получить всех поставщиков.
   * @param {{type:String, order:String}} sort Параметры сортировки
   * @param {number}                      page Номер страницы.
   * @returns {Promise<Either>}
   */
  this.getAll = (
    sort = { type: '', order: '' },
    page = 1,
  ) => axios
    .get(
      '/api/warehouse/vendors',
      {
        headers: { accept: JSON_TYPE_REQUEST },
        params: {
          sort_type: sort.type,
          sort_order: sort.order,
          page,
        },
      },
    ).then(
      async (response) => {
        const { data } = response;
        const pageCount = data.page_count;
        const vendors = data.vendors.map((item) => (new Vendor(
          item.id,
          item.name,
          item.contact,
          item.phone,
          item.email,
          item.address,
        )));
        return Either.right({
          vendors,
          pageCount,
        });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получить список поставщиков в виде таблицы.
   * @param {{type:String, order:String}} sort параметры сортировки
   * @return {Promise<Either>}
   */
  this.getAllBySheet = (sort = { type: '', order: '' }) => axios
    .get('/api/warehouse/vendors', {
      headers: { accept: EXCEL_TYPE_REQUEST },
      responseType: 'blob',
      params: {
        sort_type: sort.type,
        sort_order: sort.order,
      },
    })
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
}
